import React, { useCallback, useState } from 'react';
import { useTemplateOrCalculation } from '../../../../hooks/useTemplateOrCalculation';
import { ConfigurationSwitch } from '@ydistri/ds';
import { rtkQueryPatchStatusToConfigurationFieldStatus } from '@ydistri/utils';
import {
  DeleteProductListConfigurationPayload,
  UpdateProductListConfigurationPayload,
  useDeleteProductListConfigurationMutation,
  useUpdateProductListConfigurationMutation,
} from '../apiProductLists';
import { Space } from 'antd';
import { InfoIconTooltip } from '../../../../components/icons/InfoIconTooltip';

interface ProductListConfigurationSwitchProps {
  disabled?: boolean;
  enabled: boolean | null;
  archived: boolean;
  configured: boolean;
  productListId: number;
  calculationProductListConfiguration: boolean;
}
type LastConfigurationStatusChange = 'updateConfigurationStatus' | 'deleteConfigurationStatus';

const ProductListConfigurationSwitch: React.FC<ProductListConfigurationSwitchProps> = ({
  disabled = false,
  enabled,
  archived,
  configured,
  productListId,
  calculationProductListConfiguration,
}) => {
  const templateOrCalculation = useTemplateOrCalculation();
  const [updateConfiguration, updateConfigurationStatus] =
    useUpdateProductListConfigurationMutation();
  const [deleteConfiguration, deleteConfigurationStatus] =
    useDeleteProductListConfigurationMutation();

  const [latestAction, setLatestAction] = useState<LastConfigurationStatusChange>(
    'updateConfigurationStatus',
  );

  const latestStatus =
    latestAction === 'updateConfigurationStatus'
      ? updateConfigurationStatus
      : deleteConfigurationStatus;
  const status = rtkQueryPatchStatusToConfigurationFieldStatus(latestStatus);

  let value = false;
  if (calculationProductListConfiguration) {
    value = enabled ?? true;
  } else {
    value = enabled ?? false;
  }

  const updateProductList = useCallback(() => {
    if (!configured) {
      const updatePayload: UpdateProductListConfigurationPayload = {
        templateOrCalculation,
        productListId: productListId,
        request: {
          isEnabled: !value,
        },
      };
      setLatestAction('updateConfigurationStatus');
      updateConfiguration(updatePayload);
    } else {
      const deletePayload: DeleteProductListConfigurationPayload = {
        templateOrCalculation,
        productListId: productListId,
      };
      setLatestAction('deleteConfigurationStatus');
      deleteConfiguration(deletePayload);
    }
  }, [
    configured,
    templateOrCalculation,
    productListId,
    value,
    updateConfiguration,
    deleteConfiguration,
  ]);

  const finalValue = calculationProductListConfiguration ? !value : value;
  const finalDisabled = disabled || (!finalValue && archived);

  return (
    <Space>
      <ConfigurationSwitch
        disabled={finalDisabled}
        status={status}
        value={finalValue}
        onChange={updateProductList}
        includeEmptySpace={false}
        switchColor={calculationProductListConfiguration ? 'red' : undefined}
      />
      {finalValue && (
        <InfoIconTooltip
          content={
            calculationProductListConfiguration
              ? "Product list is disabled - products from this list won't be used in redistribution."
              : 'Product list is enabled - products from this list will be used in redistribution.'
          }
        />
      )}
    </Space>
  );
};

export default ProductListConfigurationSwitch;
